import Image from "next/image"

/**
 * The Polish CC BY 4.0 deed.
 *
 * The client specified `https://creativecommons.org/licenses/by/4.0/deed.pl/` — with a
 * trailing slash. That URL is broken in a way that is easy to miss: it 301-redirects to
 * `.../4.0/deed.deed.pl`, which falls back to and serves the ENGLISH deed. A Polish user
 * clicking a Polish licence line would land on an English page. Verified 18.09.2026,
 * re-checked 22.09.2026.
 *
 * The canonical form below (no trailing slash) returns 200 on the Polish deed, so that is
 * what ships. Re-check before "fixing" this back to the client's version.
 */
const CC_BY_40_DEED_PL = "https://creativecommons.org/licenses/by/4.0/deed.pl"

/**
 * EU funding and Creative Commons marking for the bottom of the start page (DYS-048).
 *
 * Start page only. Deliberately not on the processing screen, and not on the text display
 * screen — that is a reading surface, and a footer under the document would sit in the way
 * of the text itself.
 *
 * ---
 *
 * WHAT IS A PICTURE AND WHAT IS TEXT (DYS-054).
 *
 * All three marks arrived as PNGs in which the artwork and its wording were one flat image.
 * That is why the client reported the wording looking too narrow beside the logo on a
 * laptop: an image cannot reflow, so the words were locked to whatever width the file was
 * given, while the column around them grew. Their instruction was to set as text whatever
 * is text.
 *
 * So: **the logos are still the supplied artwork; the sentences beside them are now real
 * text.** The Incubator mark and the CC BY badge were cut out of their files — the cut
 * points found by scanning for a fully transparent column between mark and wording, not by
 * eye — and the wording they carried is typed out below. It reflows, it scales with the
 * reader's browser, a screen reader reads it, and it costs about 120 KB less.
 *
 * **The EU strip is the exception and stays exactly as supplied.** Its four captions are
 * inside the official lockup rather than beside it, so setting them as text would mean
 * redrawing a mandatory funding mark. That is a compliance decision rather than a design
 * one, and the client's answer was to leave it alone. If it is ever revisited, the artwork
 * is unchanged in git and the originals are in the tasks folder.
 *
 * Note that `next.config.mjs` sets `images: { unoptimized: true }`, so the `width`/`height`
 * props only reserve layout space — they do not resize anything. They are the real pixel
 * dimensions of the committed files, and they must be updated if the files are ever
 * re-cropped, or the reserved space will be the wrong shape and the page will jump on load.
 */
export default function FundingFooter() {
  return (
    /*
     * A hairline, not a block of colour. The footer shares the page's background, so
     * without some edge it reads as more page rather than as the end of it - but a filled
     * band would make a legal notice louder than the upload buttons, which is the wrong way
     * round.
     */
    <footer className="mt-4 border-t border-[#1E3144]/10 bg-[#FBF8FF] px-4 pb-10 pt-8">
      <div className="mx-auto flex w-full max-w-2xl flex-col items-start gap-7 text-[rgba(78,85,98,1)]">
        {/* The mandatory funding lockup, and the widest thing here: at 12:1 every pixel
            taken off its width comes straight off the four captions. It gets the whole
            column for that reason. On a phone it is small and there is no fix for that in
            CSS - a twelve-to-one lockup at 360px is what it is, and the supplied set has no
            stacked variant to swap in. */}
        <Image
          src="/images/funding/eu-strip-pl.png"
          alt="Fundusze Europejskie dla Rozwoju Społecznego, Rzeczpospolita Polska, Dofinansowane przez Unię Europejską, DGA"
          width={1310}
          height={107}
          className="h-auto w-full"
          priority={false}
        />

        {/*
          * The project statement: mark on the left, wording beside and below it.
          *
          * The supplied file set the opening sentence beside the mark and ran the rest
          * full width underneath, and that shape is kept - it is what makes the contract
          * reference read as a continuation rather than as a second statement.
          *
          * Sized against the EU strip, not against the column. The first text version
          * gave the mark 13rem, which made it half again as tall as the four marks above
          * it and left a dead gap under the opening sentence, because a tall logo and a
          * two-line sentence top-aligned side by side leave the sentence stranded at the
          * top. At ~7.5rem it stands as tall as the strip's marks - which is also the
          * proportion the supplied artwork had - and centred beside it the sentence
          * balances the logo instead of hanging off it.
          *
          * Side by side at every width. The client said the phone looked right before,
          * and before, mark and sentence shared the row; stacking them on a phone made
          * the logo a separate object in the middle of a legal notice. At 6.5rem the
          * mark leaves the sentence about 240px, which is four comfortable lines.
          *
          * Navy, not the footer's muted grey. This is the mandatory funding statement,
          * and set in the same grey as the licence boilerplate it had lost the weight it
          * carried in the artwork, where it was black.
          */}
        <div className="w-full text-[#1E3144]">
          <div className="flex flex-row items-center gap-3 sm:gap-4">
            <Image
              src="/images/funding/inkubator-logo.png"
              alt="Inkubator Innowacji Społecznych Wielkich Jutra — Dostępność +"
              width={422}
              height={205}
              className="h-auto w-[6.5rem] flex-shrink-0 sm:w-[7.5rem]"
            />
            {/* The non-breaking space keeps "Dostępność +" whole: on a phone the "+”,"
                was being left alone on a line of its own. */}
            <p className="text-xs font-bold leading-snug">
              Przygotowano w ramach Projektu „Inkubator Innowacji Społecznych Wielkich Jutra
              – Dostępność{"\u00A0"}+”,
            </p>
          </div>
          {/* The contract number and the date are identifiers, so neither may break:
              on a phone the number was being split at its hyphen into "FERS.05.01-IZ.00-"
              and "0003/23", which is not a number anyone could search for. */}
          <p className="mt-2 text-xs leading-relaxed">
            realizowanego na podstawie umowy nr{" "}
            <span className="whitespace-nowrap">FERS.05.01-IZ.00-0003/23</span> z{" "}
            <span className="whitespace-nowrap">08.02.2024 r.</span> przez DGA SA na zlecenie
            Ministerstwa Funduszy i Polityki Regionalnej w ramach programu Fundusze Europejskie
            dla Rozwoju Społecznego 2021-2027 współfinansowanego ze środków Europejskiego
            Funduszu Społecznego Plus.
          </p>
        </div>

        {/*
          * The licence: badge, then the attribution in one sentence.
          *
          * These were two statements before - the badge carried "Creative Commons Uznanie
          * Autorstwa 4.0. (CC-BY 4.0)" in its own artwork, and the sentence under it named
          * the same licence again. As images that merely looked redundant; set as text,
          * side by side, it would have read as a mistake. The licence is named once now,
          * in full, inside the attribution that CC BY actually requires.
          *
          * Author names are plain text on purpose: the markup the client supplied pointed
          * them at https://example.com, a placeholder, and dropping the link was agreed
          * rather than guessing a real destination.
          *
          * Badge beside the sentence at every width, for the same reason as the Incubator
          * mark above: stacked on a phone it read as a loose image rather than as the
          * licence mark of the line it belongs to. 88px is the size CC publishes its
          * badges at.
          */}
        <div className="flex w-full flex-row items-center gap-3 sm:gap-4">
          <Image
            src="/images/funding/cc-by-logo.png"
            alt="Znak licencji Creative Commons"
            width={421}
            height={148}
            className="h-auto w-[5.5rem] flex-shrink-0"
          />
          <p className="max-w-[38rem] text-xs leading-relaxed">
            Dyslexa © 2026 Agnieszka Sokołowska, Krzysztof Prystupa — udostępniona na licencji
            Creative Commons Uznanie Autorstwa 4.0{" "}
            {/* nowrap because the short name of a licence is one thing, and this broke as
                "CC BY" / "4.0" across two lines at the full column width. */}
            <a
              href={CC_BY_40_DEED_PL}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap underline underline-offset-2 hover:text-[rgba(85,54,109,1)]"
            >
              (CC BY 4.0)
            </a>
          </p>
        </div>
      </div>
    </footer>
  )
}
